/**
 * 이산 줌 정책 (프레임워크 비의존).
 * 온도 격자처럼 연속 scale이 표현을 깨뜨리는 도메인용.
 */

/** IR/온도 뷰 기본 배율 — 한 변 기준 2^n */
export const THERMAL_ZOOM_LEVELS = Object.freeze([1, 2, 4, 8, 16]);

/**
 * @param {number[]} [levels]
 * @returns {{
 *   levels: readonly number[],
 *   levelAt: (index: number) => number,
 *   clampIndex: (index: number) => number,
 *   indexOfNearest: (scale: number) => number,
 *   nextIndex: (index: number) => number,
 *   prevIndex: (index: number) => number,
 *   canZoomIn: (index: number) => boolean,
 *   canZoomOut: (index: number) => boolean,
 * }}
 */
export function createDiscreteZoomPolicy(levels = THERMAL_ZOOM_LEVELS) {
  const sorted = [...(levels || [])]
    .map(Number)
    .filter((n) => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b);

  if (sorted.length === 0) {
    throw new Error('createDiscreteZoomPolicy: levels must be a non-empty array of positive numbers');
  }

  const frozen = Object.freeze([...sorted]);
  const maxIndex = frozen.length - 1;

  function clampIndex(index) {
    const i = Number(index);
    if (!Number.isFinite(i)) {
      return 0;
    }
    return Math.max(0, Math.min(maxIndex, Math.round(i)));
  }

  function levelAt(index) {
    return frozen[clampIndex(index)];
  }

  function indexOfNearest(scale) {
    const k = Number(scale);
    if (!Number.isFinite(k) || k <= 0) {
      return 0;
    }
    let best = 0;
    let bestDist = Math.abs(frozen[0] - k);
    for (let i = 1; i < frozen.length; i += 1) {
      const dist = Math.abs(frozen[i] - k);
      if (dist < bestDist) {
        best = i;
        bestDist = dist;
      }
    }
    return best;
  }

  return {
    levels: frozen,
    levelAt,
    clampIndex,
    indexOfNearest,
    nextIndex(index) {
      return Math.min(maxIndex, clampIndex(index) + 1);
    },
    prevIndex(index) {
      return Math.max(0, clampIndex(index) - 1);
    },
    canZoomIn(index) {
      return clampIndex(index) < maxIndex;
    },
    canZoomOut(index) {
      return clampIndex(index) > 0;
    }
  };
}
